// AdminTeam — Gestion de l'équipe : modifier membres, spécialités, disponibilité, ajout (v5.21.0).
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { T, Avatar, StatusPill } from "./cabinetTheme";
import { getFounderToken } from "@/lib/founderApi";

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${getFounderToken()}` });

const MemberForm = ({ initial, onSave, onCancel, saving }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const field = { padding: "8px 11px", borderRadius: 9, border: "1px solid #E5E7EB", fontSize: 12.5, width: "100%", boxSizing: "border-box" };
  return (
    <div style={{ display: "grid", gap: 7, marginTop: 8 }} data-testid="member-form">
      <input style={field} placeholder="Nom (ex. Me Awa Koné)" value={f.name} onChange={set("name")} data-testid="member-form-name" />
      <input style={field} placeholder="Fonction (ex. Avocate collaboratrice)" value={f.title} onChange={set("title")} data-testid="member-form-title" />
      <input style={field} placeholder="Spécialités, séparées par des virgules" value={f.specialties} onChange={set("specialties")} data-testid="member-form-specialties" />
      <div style={{ display: "flex", gap: 7 }}>
        <select style={{ ...field, width: "auto", flex: 1 }} value={f.availability} onChange={set("availability")} data-testid="member-form-availability">
          <option value="disponible">🟢 Disponible</option>
          <option value="visio">🟠 En visio</option>
        </select>
        <select style={{ ...field, width: "auto", flex: 1 }} value={f.role} onChange={set("role")} data-testid="member-form-role">
          <option value="LAWYER">Avocat·e</option>
          <option value="LEGAL_ADVISOR">Conseiller juridique</option>
          <option value="SECRETARY">Assistant·e</option>
        </select>
      </div>
      <div style={{ display: "flex", gap: 7 }}>
        <button style={{ ...T.btnGreen, padding: "8px 14px", fontSize: 12, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={() => onSave(f)} data-testid="member-form-save">Enregistrer</button>
        <button style={{ background: "none", border: "none", color: "#6B7280", fontSize: 12, fontWeight: 700, cursor: "pointer" }} onClick={onCancel} data-testid="member-form-cancel">Annuler</button>
      </div>
    </div>
  );
};

export const AdminTeam = ({ slug, members, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async (memberId, f) => {
    setSaving(true);
    try {
      const body = { name: f.name, title: f.title, specialties: f.specialties, availability: f.availability, role: f.role };
      if (memberId) await axios.patch(`${API}/api/v3/cabinet/${slug}/admin/members/${memberId}`, body, { headers: hdr() });
      else await axios.post(`${API}/api/v3/cabinet/${slug}/admin/members`, body, { headers: hdr() });
      toast.success(memberId ? "Membre mis à jour ✓" : "Membre ajouté ✓");
      setEditing(null); setAdding(false); onChanged();
    } catch { toast.error("Enregistrement impossible"); }
    finally { setSaving(false); }
  };
  const toggleActive = async (m) => {
    try {
      await axios.patch(`${API}/api/v3/cabinet/${slug}/admin/members/${m.member_id}`, { active: m.active === false }, { headers: hdr() });
      onChanged();
    } catch { toast.error("Échec"); }
  };

  return (
    <div style={T.card} data-testid="admin-team">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={T.h2}>Équipe</h2>
        <button style={{ ...T.btnGreen, padding: "7px 13px", fontSize: 11.5 }} onClick={() => { setAdding(true); setEditing(null); }} data-testid="team-add-member">+ Ajouter un membre</button>
      </div>
      {adding && <MemberForm initial={{ name: "", title: "", specialties: "", availability: "disponible", role: "LAWYER" }} saving={saving} onCancel={() => setAdding(false)} onSave={(f) => save(null, f)} />}
      <div style={{ display: "grid", gap: 0, marginTop: 6 }}>
        {members.map((m) => (
          <div key={m.member_id} style={{ padding: "10px 0", borderBottom: "1px solid #F3F4F6", opacity: m.active === false ? 0.45 : 1 }} data-testid={`admin-member-${m.member_id}`}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <Avatar name={m.name} color={m.color} size={34} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800 }}>
                  {m.name} {m.is_demo && <span style={{ ...T.badge, ...T.badgeDemo }}>DEMO</span>} {m.active === false && <span style={{ ...T.badge, background: "#F3F4F6", color: "#6B7280" }}>Retiré</span>}
                </div>
                <div style={{ ...T.sub, fontSize: 11 }}>{m.title} · {(m.specialties || []).join(" • ")}</div>
              </div>
              <StatusPill availability={m.availability} />
              <button style={{ background: "none", border: "none", color: "#0E8A4F", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}
                onClick={() => { setEditing(editing === m.member_id ? null : m.member_id); setAdding(false); }} data-testid={`team-edit-${m.member_id}`}>Modifier</button>
              <button style={{ background: "none", border: "none", color: "#9CA3AF", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}
                onClick={() => toggleActive(m)} data-testid={`team-toggle-${m.member_id}`}>{m.active === false ? "Réactiver" : "Retirer"}</button>
            </div>
            {editing === m.member_id && (
              <MemberForm saving={saving} onCancel={() => setEditing(null)} onSave={(f) => save(m.member_id, f)}
                initial={{ name: m.name, title: m.title || "", specialties: (m.specialties || []).join(", "), availability: m.availability || "disponible", role: m.role || "LAWYER" }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
